/**
 * Dashboard Nav tab models (spec §2.2 tab 5; W4b card C3). Pure, UI-rate (4 Hz while open): Route (the plotted route,
 * next jump, ETA and travel blockers) and Nearby (known stations, nearest first, with service tags and a filter).
 * Map is a launcher with no model.
 */
import { z } from 'zod'
import type { MassLockReason, TravelMode } from '../../../../reach/runtime/types'
import { formatDuration } from '../../../format'
import { whereText, type DockedActionText, type StationDirectory, type StationService } from '../../data'
import navJson from './nav.json'

const text = z.string().min(1)
const SERVICES = ['market', 'repair', 'outfitting', 'contracts', 'chart-buyer', 'parts-vendor', 'lease-office'] as const satisfies readonly StationService[]

export const navTextSchema = z.strictObject({
  comment: z.string(),
  sections: z.strictObject({ route: text, nearby: text, map: text }),
  route: z.strictObject({
    none: text,
    to: text,
    next: text,
    jumpsLeft: text,
    jumpLeft: text,
    arrived: text,
    eta: text,
    clear: text,
    massLock: z.strictObject({ none: z.string(), station: text, relay: text, body: text, ship: text, weaponLock: text }),
    interdicted: text,
  }),
  nearby: z.strictObject({
    empty: text,
    all: text,
    courseSet: text,
    services: z.strictObject({ market: text, repair: text, outfitting: text, contracts: text, 'chart-buyer': text, 'parts-vendor': text, 'lease-office': text }),
  }),
  map: z.strictObject({ system: text, systemKey: text, galaxy: text, note: text }),
})
export type NavText = z.infer<typeof navTextSchema>

let cached: NavText | null = null
export function loadNavText(): NavText {
  cached ??= navTextSchema.parse(navJson)
  return cached
}

function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m)
}

/** What Route reads from the Reach (`state.route`, the travel state). */
export interface RouteInput {
  /** System ids of the plotted route, start first (`ReachState.route`); null = none. */
  readonly route: readonly string[] | null
  readonly hereSystemId: string | null
  readonly systemName: (id: string) => string
  readonly mode: TravelMode
  readonly massLock: MassLockReason
  /** Seconds to the nav target (0 = unknown). */
  readonly etaS: number
}

export interface RouteModel {
  /** False: no route; the section shows `none` and no Clear button. */
  readonly plotted: boolean
  readonly to: string
  readonly next: string | null
  readonly left: string
  readonly eta: string | null
  /** Blocker lines (mass-lock, interdiction). */
  readonly blockers: readonly string[]
}

export function routeModel(i: RouteInput, t: NavText = loadNavText()): RouteModel {
  const blockers: string[] = []
  if (i.mode === 'interdicted') blockers.push(t.route.interdicted)
  const lock = t.route.massLock[i.massLock]
  if (lock !== '') blockers.push(lock)
  const eta = i.etaS > 0 ? fill(t.route.eta, { time: formatDuration(i.etaS) }) : null
  const r = i.route
  if (r === null || r.length === 0) return { plotted: false, to: '', next: null, left: '', eta, blockers }
  const dest = r[r.length - 1]
  const at = i.hereSystemId === null ? -1 : r.indexOf(i.hereSystemId)
  const nextId = at >= 0 && at + 1 < r.length ? r[at + 1] : at < 0 ? r[0] : null
  const jumps = at >= 0 ? r.length - 1 - at : r.length - 1
  return {
    plotted: true,
    to: fill(t.route.to, { system: i.systemName(dest) }),
    next: nextId === null ? null : fill(t.route.next, { system: i.systemName(nextId) }),
    left: jumps <= 0 ? t.route.arrived : jumps === 1 ? t.route.jumpLeft : fill(t.route.jumpsLeft, { n: String(jumps) }),
    eta,
    blockers,
  }
}

export type NearbyFilter = 'all' | StationService

export interface NearbyRow {
  readonly id: string
  readonly name: string
  /** "this system · 8.00 km" / "2 jumps". */
  readonly where: string
  readonly services: readonly string[]
}

/** Known stations reachable by a known route, this system first by distance, then by jumps; `filter` by service. */
export function nearbyModel(dir: StationDirectory, filter: NearbyFilter, words: DockedActionText, t: NavText = loadNavText()): NearbyRow[] {
  const here = dir.hereSystemId
  const pos = dir.herePosition
  const hits = []
  for (const s of dir.stations) {
    if (filter !== 'all' && !s.services.includes(filter)) continue
    const jumps = dir.jumpsTo(s.systemId)
    if (jumps === null) continue
    let distanceM: number | null = null
    if (s.systemId === here && pos !== null) {
      const dx = s.position[0] - pos[0]
      const dy = s.position[1] - pos[1]
      const dz = s.position[2] - pos[2]
      distanceM = Math.sqrt(dx * dx + dy * dy + dz * dz)
    }
    hits.push({ station: s, jumps, distanceM })
  }
  hits.sort((a, b) => a.jumps - b.jumps || (a.distanceM ?? 0) - (b.distanceM ?? 0) || a.station.name.localeCompare(b.station.name) || (a.station.id < b.station.id ? -1 : 1))
  return hits.map((h) => ({
    id: h.station.id,
    name: h.station.name,
    where: whereText(words, h),
    services: SERVICES.filter((sv) => h.station.services.includes(sv)).map((sv) => t.nearby.services[sv]),
  }))
}

/** The filter strip: All, then each service in a fixed order. */
export const NEARBY_FILTERS: readonly NearbyFilter[] = ['all', ...SERVICES]
